import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { 
  ArrowLeft, Check, X, ChevronLeft, ChevronRight,
  Award, Clock, Target, BookOpen, Plane
} from "lucide-react";
import ExplainWithAI from "../components/ExplainWithAI";

// Helper to format percentage with 2 decimal places
const formatPercent = (value) => {
  if (value === null || value === undefined) return '0.00';
  return Number(value).toFixed(2);
};

const ExamReviewPage = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reviewLocked, setReviewLocked] = useState(false);
  const [exam, setExam] = useState(null);
  const [result, setResult] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    fetchReview();
  }, [examId]);

  const fetchReview = async () => {
    try {
      const response = await axios.get(
        `${API}/exams/${examId}/review`,
        { withCredentials: true }
      );
      setExam(response.data.exam);
      setResult(response.data.result);
      setQuestions(response.data.questions);
      
      // Achievement popup removed - badges are now shown via the proper badge system
    } catch (error) {
      console.error("Error fetching review:", error);
      // Check if it's a 403 (locked review)
      if (error.response?.status === 403) {
        setReviewLocked(true);
      } else {
        toast.error("Erro ao carregar revisão do exame");
        navigate("/dashboard");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cockpit-bg flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Show "awaiting review" message if locked
  if (reviewLocked) {
    return (
      <div className="min-h-screen bg-cockpit-bg flex items-center justify-center p-6">
        <Card className="instrument-panel max-w-lg w-full">
          <CardContent className="p-8">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 bg-orange-500/20">
                <Clock className="w-10 h-10 text-orange-400" />
              </div>

              <h2 className="font-chivo text-2xl text-cockpit-text-bright mb-4">
                A Aguardar Correção
              </h2>
              
              <p className="text-slate-300 mb-6">
                A aguardar correção do exame. Assim que estiver corrigido, poderá rever o seu exame e consultar o resultado.
              </p>

              <Button 
                onClick={() => navigate("/dashboard")}
                className="btn-cockpit"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar ao Painel
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const totalCorrect = questions.filter(q => q.is_correct).length;

  return (
    <div className="min-h-screen bg-cockpit-bg">
      {/* Header */}
      <header className="border-b border-cockpit-border bg-cockpit-panel/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate("/dashboard")}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Voltar
              </Button>
              <div>
                <h1 className="font-chivo text-xl text-cockpit-text-bright">{exam?.title}</h1>
                <p className="text-sm text-slate-500">Revisão do Exame</p>
              </div>
            </div>
            
            {/* Result Summary */}
            <div className={`px-4 py-2 rounded-lg ${
              result?.passed 
                ? 'bg-emerald-500/20 border border-emerald-500/30' 
                : 'bg-red-500/20 border border-red-500/30'
            }`}>
              <div className="flex items-center gap-3">
                {result?.passed ? (
                  <Award className="w-6 h-6 text-emerald-400" />
                ) : (
                  <X className="w-6 h-6 text-red-400" />
                )}
                <div>
                  <p className={`font-bold ${result?.passed ? 'text-emerald-400' : 'text-red-400'}`}>
                    {result?.passed ? 'APROVADO' : 'REPROVADO'}
                  </p>
                  <p className="text-xs text-slate-400">
                    {totalCorrect}/{questions.length} corretas · {formatPercent(result?.overall_accuracy)}%
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 pb-28">
        {/* Progress Indicator */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-400">
              Questão {currentIndex + 1} de {questions.length}
            </span>
            <div className="flex gap-1 flex-wrap justify-end max-w-[70%]">
              {questions.length <= 15 ? (
                // Show all dots if 15 or fewer questions
                questions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentIndex(i)}
                    className={`w-3 h-3 rounded-full transition-colors shrink-0 ${
                      i === currentIndex 
                        ? 'bg-orange-500 ring-2 ring-orange-300' 
                        : q.is_correct 
                          ? 'bg-emerald-500/50 hover:bg-emerald-500' 
                          : 'bg-red-500/50 hover:bg-red-500'
                    }`}
                    title={`Questão ${i + 1}: ${q.is_correct ? 'Correta' : 'Incorreta'}`}
                  />
                ))
              ) : (
                // Smart pagination for many questions
                <>
                  {/* First few dots */}
                  {[0, 1].map(i => (
                    <button
                      key={i}
                      onClick={() => setCurrentIndex(i)}
                      className={`w-3 h-3 rounded-full transition-colors shrink-0 ${
                        i === currentIndex 
                          ? 'bg-orange-500 ring-2 ring-orange-300' 
                          : questions[i]?.is_correct 
                            ? 'bg-emerald-500/50 hover:bg-emerald-500' 
                            : 'bg-red-500/50 hover:bg-red-500'
                      }`}
                      title={`Questão ${i + 1}: ${questions[i]?.is_correct ? 'Correta' : 'Incorreta'}`}
                    />
                  ))}
                  
                  {/* Ellipsis */}
                  {currentIndex > 3 && <span className="text-slate-500 px-0.5 text-xs">···</span>}
                  
                  {/* Dots around current */}
                  {Array.from({ length: 3 }, (_, idx) => currentIndex - 1 + idx)
                    .filter(i => i > 1 && i < questions.length - 2)
                    .map(i => (
                      <button
                        key={i}
                        onClick={() => setCurrentIndex(i)}
                        className={`w-3 h-3 rounded-full transition-colors shrink-0 ${
                          i === currentIndex 
                            ? 'bg-orange-500 ring-2 ring-orange-300' 
                            : questions[i]?.is_correct 
                              ? 'bg-emerald-500/50 hover:bg-emerald-500' 
                              : 'bg-red-500/50 hover:bg-red-500'
                        }`}
                        title={`Questão ${i + 1}: ${questions[i]?.is_correct ? 'Correta' : 'Incorreta'}`}
                      />
                    ))
                  }
                  
                  {/* Ellipsis */}
                  {currentIndex < questions.length - 4 && <span className="text-slate-500 px-0.5 text-xs">···</span>}
                  
                  {/* Last few dots */}
                  {[questions.length - 2, questions.length - 1].map(i => (
                    <button
                      key={i}
                      onClick={() => setCurrentIndex(i)}
                      className={`w-3 h-3 rounded-full transition-colors shrink-0 ${
                        i === currentIndex 
                          ? 'bg-orange-500 ring-2 ring-orange-300' 
                          : questions[i]?.is_correct 
                            ? 'bg-emerald-500/50 hover:bg-emerald-500' 
                            : 'bg-red-500/50 hover:bg-red-500'
                      }`}
                      title={`Questão ${i + 1}: ${questions[i]?.is_correct ? 'Correta' : 'Incorreta'}`}
                    />
                  ))}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Question Card */}
        {currentQuestion && (
          <Card className="instrument-panel">
            <CardHeader>
              <div className="flex items-center gap-2 mb-2">
                <span className={`px-2 py-1 rounded text-xs font-mono ${
                  currentQuestion.is_correct 
                    ? 'bg-emerald-500/20 text-emerald-400' 
                    : 'bg-red-500/20 text-red-400'
                }`}>
                  {currentQuestion.is_correct ? '✓ Correta' : '✗ Incorreta'}
                </span>
                <span className="px-2 py-1 rounded text-xs bg-slate-800 text-slate-400">
                  {currentQuestion.section_name}
                </span>
              </div>
              <CardTitle className="font-chivo text-lg text-cockpit-text-bright">
                {currentQuestion.statement}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {currentQuestion.answers.map((answer, index) => {
                // answer is now an object with 'id' and 'text'
                const answerId = answer?.id || `ans_${index}`;
                const answerText = answer?.text || answer;
                const correctAnswerId = currentQuestion.correct_answer;
                const userAnswerId = currentQuestion.user_answer_id;
                
                const isCorrect = answerId === correctAnswerId;
                const isUserAnswer = answerId === userAnswerId;
                const showAsWrong = isUserAnswer && !isCorrect;
                
                return (
                  <div
                    key={answerId}
                    className={`p-4 rounded-lg border transition-colors ${
                      isCorrect
                        ? 'bg-emerald-500/20 border-emerald-500/50'
                        : showAsWrong
                          ? 'bg-red-500/20 border-red-500/50'
                          : 'bg-slate-900/50 border-slate-800'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-mono shrink-0 ${
                        isCorrect
                          ? 'bg-emerald-500 text-white'
                          : showAsWrong
                            ? 'bg-red-500 text-white'
                            : 'bg-slate-700 text-slate-300'
                      }`}>
                        {['A', 'B', 'C', 'D'][index]}
                      </span>
                      <div className="flex-1">
                        <p className={`${
                          isCorrect ? 'text-emerald-300' : 
                          showAsWrong ? 'text-red-300' : 'text-slate-300'
                        }`}>
                          {answerText}
                        </p>
                        {isCorrect && !isUserAnswer && (
                          <p className="text-xs text-emerald-400 mt-1 flex items-center gap-1">
                            <Check className="w-3 h-3" /> Resposta correta
                          </p>
                        )}
                        {showAsWrong && (
                          <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                            <X className="w-3 h-3" /> Sua resposta
                          </p>
                        )}
                        {isUserAnswer && isCorrect && (
                          <p className="text-xs text-emerald-400 mt-1 flex items-center gap-1">
                            <Check className="w-3 h-3" /> Sua resposta (correta!)
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Note/Explanation */}
              {currentQuestion.note && (
                <div className="mt-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                  <p className="text-xs uppercase text-slate-500 mb-1">Explicação</p>
                  <p className="text-sm text-slate-300">{currentQuestion.note}</p>
                </div>
              )}

              {/* AI Explanation — only for incorrect answers */}
              {!currentQuestion.is_correct && (
                <ExplainWithAI key={currentQuestion.question_id} question={currentQuestion} />
              )}
            </CardContent>
          </Card>
        )}

        {/* Navigation — fixed bar at bottom */}
        <div className="fixed bottom-0 left-0 right-0 z-50">
          <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(to top, rgb(15,23,42) 0%, transparent 100%)" }} />
          <div className="relative bg-slate-900/95 backdrop-blur-xl border-t border-slate-700/50 shadow-2xl shadow-black/50">
            <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
              <Button
                variant="outline"
                onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
                disabled={currentIndex === 0}
                className="btn-secondary-cockpit"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Anterior
              </Button>

              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-500 font-mono hidden sm:block">
                  {currentIndex + 1} / {questions.length}
                </span>
                <Button
                  variant="outline"
                  onClick={() => navigate("/dashboard")}
                  className="btn-secondary-cockpit"
                >
                  Terminar Revisão
                </Button>
              </div>

              <Button
                variant="outline"
                onClick={() => setCurrentIndex(Math.min(questions.length - 1, currentIndex + 1))}
                disabled={currentIndex === questions.length - 1}
                className="btn-secondary-cockpit"
              >
                Seguinte
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>

        {/* Section Results Summary */}
        {result?.section_results && (
          <Card className="instrument-panel mt-8">
            <CardHeader>
              <CardTitle className="font-chivo text-lg flex items-center gap-2">
                <Target className="w-5 h-5 text-cyan-400" />
                Resultados por Disciplina
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(result.section_results).map(([sectionId, data]) => (
                  <div key={sectionId} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                    <div>
                      <p className="text-sm text-cockpit-text-bright">{data.section_name}</p>
                      <p className="text-xs text-slate-500">{data.correct}/{data.total} corretas</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`font-mono font-bold ${
                        data.passed ? 'text-emerald-400' : 'text-red-400'
                      }`}>
                        {formatPercent(data.accuracy)}%
                      </span>
                      {data.passed ? (
                        <Check className="w-5 h-5 text-emerald-400" />
                      ) : (
                        <X className="w-5 h-5 text-red-400" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default ExamReviewPage;
